import React, { Component } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import classNames from 'classnames';

import { Divider } from '@eb/eds-divider';

import { ITEMS_PROP_TYPE } from './constants';
import { getTabId, getPanelId } from './util';
import { LOCATION_PROP_TYPE } from '../../../../constants/propTypes';

import TabItem from './TabItem';

import './tabs.scss';

const TabContent = ({ focusedTab, items }) => {
    const _focusedTab = find(items, { tabKey: focusedTab });

    if (_focusedTab) {
        const { value, content } = _focusedTab;

        return (
            <div>
                <div
                    id={getPanelId(value)}
                    className={`eds-tabs__content eds-tabs__content-${focusedTab}`}
                    role="region"
                    aria-live="polite"
                    aria-labelledby={getTabId(value)}
                >
                    {content}
                </div>
            </div>
        );
    }

    return null;
};

class Tabs extends Component {
    static propTypes = {
        /**
         * items: List of items to display in the tab list.
         * Accepts value, displayName, and content
         */
        items: ITEMS_PROP_TYPE.isRequired,
        /**
         * onTabChange: Callback function to be invoked on tab change
         */
        onTabChange: PropTypes.func,
        /**
         * onTabLoad: Callback function to be invoked on tab load
         */
        onTabLoad: PropTypes.func,
        /**
         * initialSelectedTab: Initial selected tabKey
         */
        initialSelectedTab: PropTypes.string,
        /**
         * location: location object
         */
        location: LOCATION_PROP_TYPE,
    };

    constructor(props) {
        super(props);

        this.state = {
            focusedTab: props.initialSelectedTab,
        };
    }

    componentDidUpdate(prevProps) {
        const { location: prevLocation } = prevProps;
        const { location, onTabLoad, initialSelectedTab } = this.props;
        const hasSwitchedLocations =
            prevLocation.slug !== location.slug ||
            prevLocation.latitude !== location.latitude;

        if (hasSwitchedLocations) {
            const _handleOnTabLoad = () => {
                if (onTabLoad && location) {
                    onTabLoad(initialSelectedTab);
                }
            };

            this.setState(
                {
                    focusedTab: this.props.initialSelectedTab,
                },
                _handleOnTabLoad,
            );
        }
    }

    _handleSelected(tabKey) {
        const { onTabChange } = this.props;

        this.setState({ focusedTab: tabKey });

        if (onTabChange) {
            onTabChange(tabKey);
        }
    }

    _getTabItems() {
        const { items } = this.props;
        const { focusedTab } = this.state;

        const tabItems = items.map(({ value, displayName, tabKey }) => (
            <TabItem
                key={tabKey}
                value={value}
                onSelected={this._handleSelected.bind(this, tabKey)}
                displayName={displayName}
                isSelected={tabKey === focusedTab}
            />
        ));

        return tabItems;
    }

    render() {
        const { items, isNewHeader } = this.props;
        const { focusedTab } = this.state;
        const tabItems = this._getTabItems();
        const listClassNames = classNames('eds-tabs__navigation-list', {
            'eds-tabs__navigation-list--logged-in': items && items.length > 7,
        });
        const navClassnames = classNames(
            'eds-tabs__navigation eds-l-pad-vert-3 eds-l-mar-bot-3',
            {
                'eds-tab__navigation--animate-in': !isNewHeader,
            },
        );

        return (
            <div className="eds-tabs">
                <nav className={navClassnames}>
                    <ul className={listClassNames}>
                        {tabItems}
                        <Divider />
                    </ul>
                </nav>

                <TabContent focusedTab={focusedTab} items={items} />
            </div>
        );
    }
}

export default Tabs;
